import { useEffect, useState } from "react";
import { locationsApi } from "@/lib/api";
import { MapPin, Plus, Pencil, Trash2, Car, Building2, Phone, User, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import AddressAutocomplete from "@/components/AddressAutocomplete";
import { api } from "@/lib/api";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

const blankForm = {
  name: "",
  address: "",
  parking_info: "",
  nearest_hospital: "",
  contact_name: "",
  contact_phone: "",
  notes: "",
};

export default function LocationsPage() {
  const { user: me } = useAuth();
  const isAdmin = me?.role === "admin";
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(blankForm);

  const load = async () => {
    setLoading(true);
    try {
      const data = await locationsApi.list();
      setLocations(data);
    } catch {
      toast.error("Failed to load locations");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const openNew = () => {
    setEditing(null);
    setForm(blankForm);
    setDialogOpen(true);
  };

  const openEdit = (loc) => {
    setEditing(loc);
    setForm({
      name: loc.name || "",
      address: loc.address || "",
      parking_info: loc.parking_info || "",
      nearest_hospital: loc.nearest_hospital || "",
      contact_name: loc.contact_name || "",
      contact_phone: loc.contact_phone || "",
      notes: loc.notes || "",
    });
    setDialogOpen(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      toast.error("Location name is required");
      return;
    }
    try {
      if (editing) {
        const updated = await locationsApi.update(editing.id, form);
        setLocations((l) => l.map((x) => (x.id === updated.id ? updated : x)));
        toast.success(`Updated "${updated.name}"`);
      } else {
        const created = await locationsApi.create(form);
        setLocations((l) =>
          [...l, created].sort((a, b) => a.name.localeCompare(b.name))
        );
        toast.success(`Added "${created.name}"`);
      }
      setDialogOpen(false);
    } catch {
      toast.error("Save failed");
    }
  };

  const handleDelete = async (loc) => {
    try {
      await locationsApi.remove(loc.id);
      setLocations((l) => l.filter((x) => x.id !== loc.id));
      toast.success(`Deleted "${loc.name}"`);
    } catch {
      toast.error("Delete failed");
    }
  };

  return (
    <div className="min-h-screen max-w-screen-2xl mx-auto px-6 lg:px-10 py-10">
      <div className="flex items-center justify-between gap-4 mb-2 flex-wrap">
        <div>
          <div className="flex items-center gap-3 mb-2">
            <MapPin className="w-5 h-5 text-amber-500" />
            <div className="text-overline text-zinc-500">Library</div>
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl font-light text-zinc-100">
            Locations
          </h1>
          <p className="text-zinc-500 text-sm mt-2 max-w-lg">
            Reusable shoot locations. Saved here once, selectable on any call sheet.
          </p>
        </div>
        {isAdmin && (
          <button
            data-testid="new-location-btn"
            onClick={openNew}
            className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-semibold bg-amber-500 text-black hover:bg-amber-400 rounded-sm transition-all"
          >
            <Plus className="w-4 h-4" /> New Location
          </button>
        )}
      </div>

      <div className="mt-10">
        {loading ? (
          <div className="text-zinc-500">Loading…</div>
        ) : locations.length === 0 ? (
          <div className="border border-dashed border-zinc-800 rounded-md p-12 text-center">
            <MapPin className="w-7 h-7 text-amber-500 mx-auto mb-3" strokeWidth={1.5} />
            <h3 className="font-heading text-xl text-zinc-100 mb-1">
              No locations saved
            </h3>
            <p className="text-zinc-500 text-sm">
              Build your library — locations show up on every call sheet selector.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {locations.map((loc, idx) => (
              <div
                key={loc.id}
                data-testid={`location-card-${loc.id}`}
                style={{ animationDelay: `${idx * 50}ms` }}
                className="animate-fade-up group rounded-md border border-zinc-800 bg-zinc-900 hover:border-amber-500/40 transition-colors p-5"
              >
                <div className="flex items-start justify-between gap-2 mb-3">
                  <h3 className="font-heading text-lg text-zinc-100 truncate flex-1">
                    {loc.name}
                  </h3>
                  <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    {isAdmin && (
                      <>
                        <button
                          data-testid={`edit-location-${loc.id}`}
                          onClick={() => openEdit(loc)}
                          className="p-1.5 rounded-sm text-zinc-500 hover:text-zinc-100 hover:bg-zinc-800"
                        >
                          <Pencil className="w-3.5 h-3.5" />
                        </button>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <button
                              data-testid={`delete-location-${loc.id}`}
                              className="p-1.5 rounded-sm text-zinc-500 hover:text-rose-400 hover:bg-zinc-800"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </AlertDialogTrigger>
                          <AlertDialogContent className="bg-zinc-950 border-zinc-800 text-zinc-100">
                            <AlertDialogHeader>
                              <AlertDialogTitle>Delete &ldquo;{loc.name}&rdquo;?</AlertDialogTitle>
                              <AlertDialogDescription className="text-zinc-500">
                                This won&apos;t affect projects already using it &mdash; just removes
                                it from the library.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel className="bg-transparent border-zinc-800 text-zinc-300 hover:bg-zinc-900">
                                Cancel
                              </AlertDialogCancel>
                              <AlertDialogAction
                                onClick={() => handleDelete(loc)}
                                className="bg-rose-500 text-zinc-50 hover:bg-rose-600"
                              >
                                Delete
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </>
                    )}
                  </div>
                </div>

                <div className="space-y-2 text-sm">
                  {loc.address && (
                    <div className="flex items-start gap-2 text-zinc-400">
                      <MapPin className="w-3.5 h-3.5 mt-0.5 shrink-0 text-amber-500/70" />
                      <span className="line-clamp-2">{loc.address}</span>
                    </div>
                  )}
                  {loc.parking_info && (
                    <div className="flex items-start gap-2 text-zinc-500 text-xs">
                      <Car className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                      <span className="line-clamp-2">{loc.parking_info}</span>
                    </div>
                  )}
                  {loc.nearest_hospital && (
                    <div className="flex items-start gap-2 text-zinc-500 text-xs">
                      <Building2 className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                      <span className="line-clamp-2">{loc.nearest_hospital}</span>
                    </div>
                  )}
                  {(loc.contact_name || loc.contact_phone) && (
                    <div className="pt-2 mt-2 border-t border-zinc-800/60 flex items-center gap-3 text-xs text-zinc-400">
                      {loc.contact_name && (
                        <span className="flex items-center gap-1">
                          <User className="w-3 h-3" />
                          {loc.contact_name}
                        </span>
                      )}
                      {loc.contact_phone && (
                        <span className="flex items-center gap-1 font-mono">
                          <Phone className="w-3 h-3" />
                          {loc.contact_phone}
                        </span>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-zinc-950 border-zinc-800 text-zinc-100 sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl font-light">
              {editing ? "Edit Location" : "New Location"}
            </DialogTitle>
            <DialogDescription className="text-zinc-500">
              Used on call sheets to fill in set, parking, and hospital info.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSave} className="space-y-3">
            <Field label="Name" required>
              <Input
                data-testid="location-name-input"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Studio A, Brooklyn"
                className="bg-zinc-950 border-zinc-800 focus-visible:ring-amber-500"
              />
            </Field>
            <Field label="Address">
              <AddressAutocomplete
                testId="location-address-input"
                value={form.address}
                onChange={(v) => setForm({ ...form, address: v })}
                placeholder="Start typing an address…"
              />
            </Field>
            <Field label="Parking Info">
              <Textarea
                data-testid="location-parking-input"
                value={form.parking_info}
                onChange={(e) => setForm({ ...form, parking_info: e.target.value })}
                placeholder="Free parking in lot"
                className="bg-zinc-950 border-zinc-800 focus-visible:ring-amber-500 min-h-[60px]"
              />
            </Field>
            <Field label="Nearest Hospital">
              <HospitalField
                value={form.nearest_hospital}
                onChange={(v) => setForm({ ...form, nearest_hospital: v })}
                referenceAddress={form.address}
              />
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Contact Name">
                <Input
                  data-testid="location-contact-name-input"
                  value={form.contact_name}
                  onChange={(e) => setForm({ ...form, contact_name: e.target.value })}
                  className="bg-zinc-950 border-zinc-800 focus-visible:ring-amber-500"
                />
              </Field>
              <Field label="Contact Phone">
                <Input
                  data-testid="location-contact-phone-input"
                  value={form.contact_phone}
                  onChange={(e) => setForm({ ...form, contact_phone: e.target.value })}
                  placeholder="(555) 123-4567"
                  className="bg-zinc-950 border-zinc-800 focus-visible:ring-amber-500"
                />
              </Field>
            </div>
            <Field label="Notes">
              <Textarea
                data-testid="location-notes-input"
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                className="bg-zinc-950 border-zinc-800 focus-visible:ring-amber-500 min-h-[60px]"
              />
            </Field>
            <DialogFooter className="pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setDialogOpen(false)}
                className="bg-transparent border-zinc-800 text-zinc-300 hover:bg-zinc-900 hover:text-zinc-100"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                data-testid="save-location-btn"
                className="bg-amber-500 text-black hover:bg-amber-400 rounded-sm"
              >
                {editing ? "Save Changes" : "Add Location"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-overline text-zinc-500">
        {label}
        {required && <span className="text-amber-500 ml-1">*</span>}
      </Label>
      {children}
    </div>
  );
}

function HospitalField({ value, onChange, referenceAddress }) {
  const [loading, setLoading] = useState(false);
  const handleAutoFind = async () => {
    if (!referenceAddress || referenceAddress.trim().length < 5) {
      toast.error("Fill in the location address first");
      return;
    }
    setLoading(true);
    try {
      const res = await api.get("/locations/nearest-hospital", {
        params: { address: referenceAddress },
      });
      const n = res.data?.nearest;
      if (n) {
        onChange(n.label);
        toast.success(`Found · ${n.name} (${n.distance_km} km)`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No hospital found nearby");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-2">
      <Input
        data-testid="location-hospital-input"
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder="Methodist Dallas Medical Center, 1441 N Beckley Ave…"
        className="bg-zinc-950 border-zinc-800 focus-visible:ring-amber-500"
      />
      <button
        type="button"
        data-testid="autofind-hospital-btn"
        onClick={handleAutoFind}
        disabled={loading || !referenceAddress}
        title={
          !referenceAddress
            ? "Add the location address first"
            : "Find nearest hospital via OpenStreetMap"
        }
        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs bg-amber-500/10 border border-amber-500/30 text-amber-400 hover:bg-amber-500/20 rounded-sm disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
      >
        {loading ? (
          <>
            <Loader2 className="w-3 h-3 animate-spin" /> Finding…
          </>
        ) : (
          <>
            <Building2 className="w-3 h-3" /> Auto-find nearest
          </>
        )}
      </button>
    </div>
  );
}
